import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Car } from "lucide-react";
import { MaskLines } from "./Motion";
import { HeroVisual } from "./HeroVisual";

const fade = (d) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] } });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const isLg = typeof window !== "undefined" && window.innerWidth >= 1024;
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} className="relative overflow-hidden pt-[104px] sm:pt-32 pb-16 sm:pb-24 bg-[#F7F9FC] grain" data-testid="hero-section">
      <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#2F7FF0]/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={isLg ? { y: yText } : undefined} className="lg:col-span-7">
          <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full border border-[#2F7FF0]/30 bg-white px-3.5 py-1.5" data-testid="coming-soon-badge">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-[#2F7FF0] opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#2F7FF0]" /></span>
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#13296B]">App coming soon</span>
          </motion.div>
          <MaskLines
            testId="marketshipt-hero-heading"
            lines={["Drive.", "Haul.", <span key="e">Earn<span className="text-[#2F7FF0]">.</span></span>]}
            className="font-display font-extrabold text-[#13296B] tracking-[-0.04em] leading-[0.88] text-[19vw] sm:text-[8.5rem] lg:text-[9.5rem] mt-6"
          />
          <motion.p {...fade(0.75)} className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600">
            Marketgetr is the new on-demand pickup &amp; delivery app. Bought it on Marketplace or from a private seller? A nearby driver picks it up and brings it to you.
            <strong className="text-[#13296B]"> We're recruiting our first drivers now. Sign up to get in before launch.</strong>
          </motion.p>
          <motion.div {...fade(0.9)} className="mt-9 flex flex-wrap gap-3">
            <a href="#signup" data-testid="marketshipt-hero-driver-cta" className="group inline-flex items-center gap-2 rounded-full bg-[#13296B] text-white font-bold px-7 py-4 hover:bg-[#2F7FF0] transition-colors duration-300">
              <Car className="h-5 w-5" /> Sign up to drive
              <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </a>
            <a href="#signup" data-role="customer" data-testid="marketshipt-hero-customer-cta" className="inline-flex items-center rounded-full border-2 border-[#13296B]/15 bg-white text-[#13296B] font-bold px-7 py-4 hover:border-[#13296B] transition-colors duration-300">
              Get launch updates
            </a>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 [perspective:1200px]">
          <HeroVisual rx={rx} ry={ry} />
        </div>
      </div>
    </section>
  );
};
